import API_BASE from '../config';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, LogOut, RefreshCw } from "lucide-react";
import axios from "axios";

const WARNING_BEFORE_MS  = 5 * 60 * 1000;   // Show warning 5 min before expiry
const CHECK_INTERVAL_MS  = 30 * 1000;        // Check every 30 seconds
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes inactivity = logout
const INACTIVITY_WARNING_MS = 25 * 60 * 1000; // Show warning at 25 minutes

function getTokenExpiry(): number | null {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

export default function SessionTimeout() {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft,    setTimeLeft]    = useState(300);
  const [extending,   setExtending]   = useState(false);
  const lastActivity                  = useRef(Date.now());

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      lastActivity.current = Date.now();
      if (showWarning) setShowWarning(false);
    };

    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach(e => window.addEventListener(e, updateActivity));
    return () => events.forEach(e => window.removeEventListener(e, updateActivity));
  }, [showWarning]);

  const handleSignOut = useCallback(() => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  }, []);

  const handleExtend = useCallback(async () => {
    setExtending(true);
    try {
      const res = await axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.status === 200) {
        lastActivity.current = Date.now();
        setShowWarning(false);
        setExtending(false);
      }
    } catch {
      handleSignOut();
    }
  }, [handleSignOut]);

  useEffect(() => {
    const check = () => {
      // Check JWT expiry
      const expiry = getTokenExpiry();
      if (!expiry) return;

      const now       = Date.now();
      const remaining = expiry - now;

      if (remaining <= 0) {
        handleSignOut();
        return;
      }

      // Check inactivity
      const inactiveDuration = now - lastActivity.current;

      if (inactiveDuration >= INACTIVITY_LIMIT_MS) {
        handleSignOut();
        return;
      }

      if (inactiveDuration >= INACTIVITY_WARNING_MS || remaining <= WARNING_BEFORE_MS) {
        const timeLeftMs = Math.min(
          INACTIVITY_LIMIT_MS - inactiveDuration,
          remaining
        );
        setShowWarning(true);
        setTimeLeft(Math.floor(timeLeftMs / 1000));
      } else {
        setShowWarning(false);
      }
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [handleSignOut]);

  // Countdown timer
  useEffect(() => {
    if (!showWarning) return;
    const countdown = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleSignOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdown);
  }, [showWarning, handleSignOut]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <AnimatePresence>
      {showWarning && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm px-6"
          >
            <div className="bg-[#0f172a] border border-white/10 rounded-[2.5rem] p-8 shadow-2xl shadow-black/60 text-center">
              <div className="w-16 h-16 bg-amber-500/10 border border-amber-500/20 rounded-[1.5rem] flex items-center justify-center mx-auto mb-6">
                <Clock size={32} className="text-amber-400" />
              </div>
              <p className="text-[9px] font-black uppercase tracking-[0.3em] text-amber-500 mb-3">
                Session Expiring
              </p>
              <h2 className="text-2xl font-black text-white tracking-tighter uppercase mb-3">
                Still There?
              </h2>
              <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 mb-6">
                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-2">
                  Session expires in
                </p>
                <p className="text-4xl font-black text-white tracking-tighter">
                  {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
                </p>
              </div>
              <p className="text-slate-400 text-xs font-medium leading-relaxed mb-6">
                You've been inactive for a while. Stay logged in to continue working on your assessment.
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleExtend}
                  disabled={extending}
                  className="w-full py-4 bg-red-600 rounded-2xl font-black text-xs text-white uppercase tracking-widest hover:bg-red-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {extending ? <RefreshCw size={14} className="animate-spin" /> : <><RefreshCw size={14} />Stay Logged In</>}
                </button>
                <button
                  onClick={handleSignOut}
                  className="w-full py-3 border border-white/10 rounded-2xl font-black text-xs text-slate-400 uppercase tracking-widest hover:border-white/20 hover:text-white transition-all flex items-center justify-center gap-2"
                >
                  <LogOut size={14} />Sign Out
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}